import { useMemo, useState } from 'react'
import './VisitorQueue.css'

/** 'dd.mm.yyyy, hh:mm:ss' — the format the gatehouse list has always used. */
const stamp = (iso) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

const COLUMNS = [
  { key: 'reference',    label: 'Referenznummer' },
  { key: 'company',      label: 'Spedition' },
  { key: 'phone',        label: 'Handynummer' },
  { key: 'email',        label: 'EMail' },
  { key: 'arrivedAt',    label: 'Ankunft' },
  { key: 'destination',  label: 'Status' },
  { key: 'flowMode',     label: 'Prozess' },
]

/**
 * The queue view — the same visitors as the day grid, as a list.
 *
 * Offered because not every site thinks in ramps and time slots. A gatehouse with two
 * gates wants a vehicle list it can read top to bottom: reference number, haulier,
 * contact details, when they arrived, which gate they were sent to. The grid answers
 * "what is free when"; this answers "who is here and where did we send them", and for
 * many tenants that is the whole job.
 *
 * Reads the same board payload and calls the same assign/release endpoints — only the
 * presentation differs, so neither view can drift from the other's idea of the truth.
 */
export default function VisitorQueue({
  queue, destinations, destinationsEnabled, pages, showUnfinished = true,
  readOnly = false,
  busy, onAssignDestination, onAssignPage, onRelease,
}) {
  const [sort, setSort] = useState({ key: 'arrivedAt', dir: 'desc' })

  const toggleSort = (key) =>
    setSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' })

  const rows = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1
    return [...queue].sort((a, b) => {
      const av = a[sort.key] ?? ''
      const bv = b[sort.key] ?? ''
      // Timestamps compare correctly as ISO strings; everything else is plain text,
      // where localeCompare keeps umlauts in the order a German reader expects.
      if (sort.key === 'arrivedAt') return av < bv ? -dir : av > bv ? dir : 0
      return String(av).localeCompare(String(bv), 'de', { numeric: true }) * dir
    })
  }, [queue, sort])

  // The visitor standing at the terminal right now: the one the gate attendant is
  // most likely talking to. Deliberately not "the newest arrival", which would move
  // under your hands while you are dealing with someone else.
  const current = queue.find(v => v.awaitingSince) ?? null

  const sortMark = (key) => sort.key === key ? (sort.dir === 'asc' ? '▲' : '▼') : ''

  return (
    <div className="vq">
      {/* ── Aktuelle Anmeldung ── */}
      <div className="card vq-current">
        <div className="section-title">Aktuelle Anmeldung</div>
        {current ? (
          <div className="vq-current-grid">
            <Field label="Referenznummer" value={current.reference} />
            <Field label="Spedition"      value={current.company} />
            <Field label="Handynummer"    value={current.phone} />
            <Field label="EMail"          value={current.email} />
          </div>
        ) : (
          // An empty panel would read as broken, so it says what it is waiting for.
          <p className="vq-empty">
            Derzeit wartet niemand am Terminal auf eine Zuweisung. Sobald ein Fahrer den
            Informationsschritt erreicht, erscheinen seine Daten hier.
          </p>
        )}
      </div>

      {/* ── Warteschlange ── */}
      <div className="card vq-table-card">
        <div className="section-title vq-title">
          Warteschlange
          <span className="vq-badge">{queue.length}</span>
        </div>

        {queue.length === 0 ? (
          <p className="vq-empty">
            Für diesen Tag sind keine Fahrzeuge erfasst.
          </p>
        ) : (
          <div className="vq-scroll">
            <table className="vq-table">
              <thead>
                <tr>
                  {COLUMNS.map(c => (
                    <th key={c.key} onClick={() => toggleSort(c.key)} className="vq-th">
                      {c.label} <span className="vq-sort">{sortMark(c.key)}</span>
                    </th>
                  ))}
                  <th className="vq-th vq-th--action">Zuweisung</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(v => (
                  <tr
                    key={v.sessionId}
                    className={`vq-row${v.awaitingSince ? ' vq-row--waiting' : ''}${v.checkedIn ? '' : ' vq-row--inflow'}`}
                  >
                    <td className="vq-ref">{v.reference || '—'}</td>
                    <td>{v.company || v.name || '—'}</td>
                    <td>{v.phone || '—'}</td>
                    <td className="vq-email">{v.email || '—'}</td>
                    <td className="vq-stamp">{stamp(v.arrivedAt)}</td>
                    <td>
                      {/* A held destination is a pill, an info page is plain text, and
                          nothing is a dash. Three states that mean different things —
                          occupying a place, having been shown a page, and neither — so
                          they no longer differ by font weight alone. */}
                      {v.destination
                        ? <span className="vq-status vq-status--held">{v.destination}</span>
                        : v.lastDestination
                          // Freed, but the day's record of where this visitor was does
                          // not disappear with the assignment. Past tense and no pill,
                          // so it can never be mistaken for a ramp still being held.
                          ? <span
                              className="vq-status vq-status--past"
                              title={`Zielort freigegeben${v.lastDestinationReleasedAt ? ` um ${stamp(v.lastDestinationReleasedAt)}` : ''}`
                                     + `${v.lastDestinationReleasedBy ? ` von ${v.lastDestinationReleasedBy}` : ''}`}
                            >
                              war: {v.lastDestination}
                            </span>
                          : v.shownPageName
                            ? <span className="vq-status vq-status--page">{v.shownPageName}</span>
                            : <span className="vq-status-none">—</span>}
                      {v.reserved && <span className="vq-reserved">vorgemerkt</span>}
                      {/* Still holding a destination from an earlier day. Says so
                          rather than appearing as a today row with half its columns
                          empty — there is no check-in from today to fill them. */}
                      {v.carriedOver && (
                        <span className="vq-carried" title={`Zuweisung vom ${v.visitDate}`}>
                          aus Vortag
                        </span>
                      )}
                      {/* Holding a destination with no completed check-in. Two cases,
                          because they call for opposite reactions: still at the terminal
                          is normal and needs nobody, while a session nobody is driving
                          any more is a ramp held for somebody who may not be there. Same
                          wording as the grid chip, so the two views agree. */}
                      {/* The terminal reset itself or they pressed Abbrechen, and the
                          kiosk said so. Two very different situations, told apart by
                          whether this visitor had already been given their answer:

                          shown something — they know where to go, and at a site where
                          the gate is opened by hand after a call they are very likely
                          standing there now. Calling that "abgebrochen" is wrong and
                          would have staff chasing a driver who is already at the ramp.

                          shown nothing — they stopped at the safety rules or earlier,
                          with no information and no way in. */}
                      {/* Refused by the server (Personenzulassung / Lieferavis) — checked
                          first: such a visitor may also have been shown a page, and must
                          not read as "Check-in offen, may be on site". */}
                      {v.refusedAt && !v.checkedIn ? (
                        <span
                          className="vq-abandoned"
                          title="Der Check-in wurde vom System abgelehnt. Der Besucher hat keine Zufahrt erhalten."
                        >
                          Check-in abgelehnt{v.refusedReason ? ` · ${v.refusedReason}` : ''}
                          {' · '}{new Date(v.refusedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      ) : v.abandonedAt && !v.checkedIn ? (
                        (v.shownPageName || v.destination) ? (
                          <span
                            className="vq-inflow-note"
                            title={'Dem Besucher wurde seine Information bereits angezeigt, der '
                                 + 'Check-in wurde aber nicht abgeschlossen. Das heißt nicht, dass '
                                 + 'niemand da ist — wird das Tor nach einem Anruf von Hand '
                                 + 'geöffnet, steht der Fahrer dort.'}
                          >
                            Check-in offen
                          </span>
                        ) : (
                          <span
                            className="vq-abandoned"
                            title={'Der Besucher hat den Vorgang abgebrochen oder ist weggegangen — '
                                 + 'das Terminal hat sich zurückgesetzt, bevor ihm etwas angezeigt '
                                 + 'wurde. Kein Check-in.'}
                          >
                            abgebrochen{v.abandonedStep ? ` · ${v.abandonedStep}` : ''}
                            {' · '}{new Date(v.abandonedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )
                      ) : v.unfinishedCheckin && v.fromLiveSession && (
                        <span
                          className="vq-inflow-note"
                          title="Der Besucher steht noch am Terminal. Der Check-in ist noch nicht abgeschlossen."
                        >
                          im Ablauf
                        </span>
                      )}
                      {showUnfinished && v.unfinishedCheckin && !v.fromLiveSession && (
                        <span
                          className="vq-carried"
                          title={'Der Check-in wurde nie abgeschlossen. Das heißt nicht zwingend, '
                               + 'dass niemand da ist — wurde das Tor nach einem Anruf von Hand '
                               + 'geöffnet, steht der Fahrer dort. Bitte prüfen und ggf. freigeben.'}
                        >
                          ohne Check-in — prüfen
                        </span>
                      )}
                    </td>
                    <td>{v.flowMode || '—'}</td>

                    {/* One control per row, matching whichever kind of answer this
                        visitor's flow takes. A destination carries its own info page,
                        so the two are alternatives and never both appear. */}
                    <td className="vq-action">
                      {readOnly ? (
                        // Looking at an earlier day: nothing here is still a decision.
                        <span className="vq-status-none">—</span>
                      ) : destinationsEnabled && destinations.length > 0 ? (
                        <select
                          className="form-control vq-select"
                          value=""
                          disabled={busy === v.sessionId}
                          onChange={e => e.target.value && onAssignDestination(v, e.target.value)}
                        >
                          <option value="">
                            {v.destination ? '— umsetzen —' : '— Zielort zuweisen —'}
                          </option>
                          {destinations.map(d => (
                            <option key={d.id} value={d.id} disabled={d.full && d.id !== v.destinationId}>
                              {d.displayName}{d.full ? ' (voll)' : ''}
                            </option>
                          ))}
                        </select>
                      ) : v.pageAssignable && pages.length > 0 ? (
                        <select
                          className="form-control vq-select"
                          value={v.assignedPageId ?? ''}
                          disabled={busy === v.sessionId}
                          onChange={e => onAssignPage(v.sessionId, e.target.value)}
                        >
                          <option value="">— Seite zuweisen —</option>
                          {pages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      ) : (
                        <span className="vq-status-none">—</span>
                      )}

                      {!readOnly && v.assignmentId && (
                        <button
                          className="vq-release"
                          disabled={busy === v.assignmentId}
                          onClick={() => onRelease(v.assignmentId)}
                          title="Zielort freigeben"
                        >
                          ×
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function Field({ label, value }) {
  return (
    <div className="vq-field">
      <div className="vq-field-label">{label}:</div>
      <div className="vq-field-value">{value || '—'}</div>
    </div>
  )
}
